export const PROMO_STATE_CHANGE_EVENT = "ww:promo-state-change";
export const DISCOUNTED_STAT_PROMO_USED_EVENT = "ww:discounted-stat-promo-used";
export const TAP_IN_PROMO_UNAVAILABLE_EVENT = "ww:tap-in-promo-unavailable";

const BOOST_COOKIE_NAME = "boost";
const BOOST_STORAGE_KEY = "boost";
const AUTO_SUNDAY_BOOST_STORAGE_KEY = "ww_logged_out_sunday_boost_auto";
export const SUNDAY_BOOST_PERCENT = 20;
const DEFAULT_BOOST_MAX_AGE = 24 * 60 * 60;
const DEFAULT_RISK_FREE_MAX_AGE = 24 * 60 * 60;
const RISK_FREE_COOKIE_NAMES = ["risk-free", "risk_free"];
const RISK_FREE_STORAGE_KEY = "risk_free";
const RISK_FREE_PROMO_DAY = "Thursday";
const RISK_FREE_QUERY_NAMES = ["risk-free", "risk_free"];
const DISCOUNTED_STAT_PROMO_STORAGE_KEY = "ww_discounted_stat_promo_used";
const DISCOUNTED_STAT_PROMO_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

const canUseDom = () => typeof window !== "undefined" && typeof document !== "undefined";

export const isSundayBoostLive = (date = new Date()) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      timeZone: "America/Los_Angeles"
    }).format(date) === "Sunday";
  } catch (e) {
    return date.getDay() === 0;
  }
};

const parsePositiveNumber = (value) => {
  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

const isRiskFreePromoLive = () => {
  if (!canUseDom()) {
    return false;
  }

  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      timeZone: "America/Los_Angeles"
    }).format(new Date()) === RISK_FREE_PROMO_DAY;
  } catch (e) {
    return new Date().getDay() === 4;
  }
};

export const getCookie = (name) => {
  if (!canUseDom()) {
    return "";
  }

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    return parts.pop().split(";").shift() || "";
  }

  return "";
};

const setCookie = (name, value, maxAgeSeconds = null) => {
  if (!canUseDom()) {
    return;
  }

  const maxAge = Number.isFinite(maxAgeSeconds) ? `; max-age=${maxAgeSeconds}` : "";
  document.cookie = `${name}=${value}; path=/${maxAge}`;
};

const clearCookie = (name) => {
  if (!canUseDom()) {
    return;
  }

  document.cookie = `${name}=; path=/; max-age=0`;
};

const dispatchPromoStateChange = (detail = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.CustomEvent === "function") {
    window.dispatchEvent(new window.CustomEvent(PROMO_STATE_CHANGE_EVENT, { detail }));
    return;
  }

  if (typeof document !== "undefined" && typeof document.createEvent === "function") {
    const event = document.createEvent("CustomEvent");
    event.initCustomEvent(PROMO_STATE_CHANGE_EVENT, false, false, detail);
    window.dispatchEvent(event);
  }
};

const dispatchDiscountedStatPromoUsed = () => {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.CustomEvent === "function") {
    window.dispatchEvent(new window.CustomEvent(DISCOUNTED_STAT_PROMO_USED_EVENT));
    return;
  }

  if (typeof document !== "undefined" && typeof document.createEvent === "function") {
    const event = document.createEvent("CustomEvent");
    event.initCustomEvent(DISCOUNTED_STAT_PROMO_USED_EVENT, false, false, {});
    window.dispatchEvent(event);
  }
};

const getDiscountedStatPromoServerValue = () => {
  if (!canUseDom()) {
    return null;
  }

  const usageField = document.getElementById("discounted-stat-promo-used");
  if (!usageField) {
    return null;
  }

  return usageField.value === "1";
};

const clearStoredDiscountedStatPromoUse = () => {
  if (!canUseDom()) {
    return;
  }

  try {
    window.localStorage.removeItem(DISCOUNTED_STAT_PROMO_STORAGE_KEY);
  } catch (e) {
    // Storage failures should not override the authoritative server state.
  }
};

const getStoredDiscountedStatPromoUse = () => {
  if (!canUseDom()) {
    return false;
  }

  try {
    const rawValue = window.localStorage.getItem(DISCOUNTED_STAT_PROMO_STORAGE_KEY);
    if (!rawValue) {
      return false;
    }

    const parsedValue = JSON.parse(rawValue);
    const expiresAt = Number(parsedValue?.expiresAt || 0);
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      window.localStorage.removeItem(DISCOUNTED_STAT_PROMO_STORAGE_KEY);
      return false;
    }

    return true;
  } catch (e) {
    return false;
  }
};

export const hasRecentDiscountedStatPromoUse = () => {
  const serverValue = getDiscountedStatPromoServerValue();

  if (serverValue === false) {
    clearStoredDiscountedStatPromoUse();
    return false;
  }

  return serverValue === true || getStoredDiscountedStatPromoUse();
};

export const markDiscountedStatPromoUsed = () => {
  if (!canUseDom()) {
    return;
  }

  try {
    window.localStorage.setItem(
      DISCOUNTED_STAT_PROMO_STORAGE_KEY,
      JSON.stringify({ expiresAt: Date.now() + DISCOUNTED_STAT_PROMO_MAX_AGE })
    );
  } catch (e) {
    // Ignore storage failures.
  }

  const usageField = document.getElementById("discounted-stat-promo-used");
  if (usageField) {
    usageField.value = "1";
  }

  dispatchDiscountedStatPromoUsed();
};

const getRiskFreeQueryValue = () => {
  if (!canUseDom()) {
    return "";
  }

  const searchParams = new URLSearchParams(window.location.search);
  return RISK_FREE_QUERY_NAMES.map((name) => searchParams.get(name)).find(Boolean) || "";
};

const clearRiskFreeQueryValue = () => {
  if (!canUseDom() || !window.history?.replaceState) {
    return;
  }

  const url = new URL(window.location.href);
  RISK_FREE_QUERY_NAMES.forEach((name) => url.searchParams.delete(name));
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
};

export const getBoostValue = () => {
  if (!canUseDom()) {
    return 0;
  }

  const urlBoost = parsePositiveNumber(new URLSearchParams(window.location.search).get("boost"));
  let cookieBoost = parsePositiveNumber(getCookie(BOOST_COOKIE_NAME));
  let sessionBoost = 0;
  let autoSundayBoost = false;

  try {
    sessionBoost = parsePositiveNumber(window.sessionStorage.getItem(BOOST_STORAGE_KEY));
    autoSundayBoost = window.sessionStorage.getItem(AUTO_SUNDAY_BOOST_STORAGE_KEY) === "1";
  } catch (e) {
    // Storage can be unavailable in privacy-restricted browsers.
  }

  const loggedOut = Boolean(document.body) && !document.body.classList.contains("logged-in");
  const sundayBoostLive = isSundayBoostLive();

  if (autoSundayBoost && !sundayBoostLive) {
    try {
      window.sessionStorage.removeItem(BOOST_STORAGE_KEY);
      window.sessionStorage.removeItem(AUTO_SUNDAY_BOOST_STORAGE_KEY);
    } catch (e) {
      // Ignore storage failures.
    }
    clearCookie(BOOST_COOKIE_NAME);
    cookieBoost = 0;
    sessionBoost = 0;
  }

  let activeBoost = urlBoost || cookieBoost || sessionBoost;

  if (loggedOut && sundayBoostLive && activeBoost <= 0) {
    activeBoost = SUNDAY_BOOST_PERCENT;
    setCookie(BOOST_COOKIE_NAME, String(activeBoost), DEFAULT_BOOST_MAX_AGE);
    try {
      window.sessionStorage.setItem(AUTO_SUNDAY_BOOST_STORAGE_KEY, "1");
    } catch (e) {
      // The cookie is enough for the server-authoritative payout quote.
    }
  }

  if (activeBoost > 0) {
    try {
      window.sessionStorage.setItem(BOOST_STORAGE_KEY, String(activeBoost));
    } catch (e) {
      // Ignore storage failures.
    }
  }

  return activeBoost;
};

export const setBoostValue = (value, maxAgeSeconds = DEFAULT_BOOST_MAX_AGE) => {
  const normalizedValue = parsePositiveNumber(value);
  if (!normalizedValue || !canUseDom()) {
    return;
  }

  try {
    window.sessionStorage.setItem(BOOST_STORAGE_KEY, String(normalizedValue));
    window.sessionStorage.removeItem(AUTO_SUNDAY_BOOST_STORAGE_KEY);
  } catch (e) {
    // Ignore storage failures.
  }

  setCookie(BOOST_COOKIE_NAME, String(normalizedValue), maxAgeSeconds);
  dispatchPromoStateChange({ type: "boost", value: normalizedValue });
};

export const clearBoostValue = () => {
  if (!canUseDom()) {
    return;
  }

  try {
    window.sessionStorage.removeItem(BOOST_STORAGE_KEY);
    window.sessionStorage.removeItem(AUTO_SUNDAY_BOOST_STORAGE_KEY);
  } catch (e) {
    // Ignore storage failures.
  }

  clearCookie(BOOST_COOKIE_NAME);
  dispatchPromoStateChange({ type: "boost", value: 0 });
};

export const getRiskFreeValue = () => {
  if (!canUseDom()) {
    return "";
  }

  if (!isRiskFreePromoLive()) {
    try {
      window.sessionStorage.removeItem(RISK_FREE_STORAGE_KEY);
    } catch (e) {
      // Ignore storage failures.
    }

    RISK_FREE_COOKIE_NAMES.forEach((cookieName) => {
      clearCookie(cookieName);
    });
    return "";
  }

  let storedRiskFree = "";
  try {
    storedRiskFree = window.sessionStorage.getItem(RISK_FREE_STORAGE_KEY) || "";
  } catch (e) {
    // Ignore storage failures.
  }

  const urlRiskFree = String(getRiskFreeQueryValue() || "").trim();
  const cookieRiskFree = RISK_FREE_COOKIE_NAMES.map((name) => getCookie(name)).find(Boolean) || "";
  const activeRiskFree = urlRiskFree || storedRiskFree || cookieRiskFree;

  if (activeRiskFree) {
    try {
      window.sessionStorage.setItem(RISK_FREE_STORAGE_KEY, activeRiskFree);
    } catch (e) {
      // Ignore storage failures.
    }

    if (urlRiskFree) {
      RISK_FREE_COOKIE_NAMES.forEach((cookieName) => {
        setCookie(cookieName, urlRiskFree, DEFAULT_RISK_FREE_MAX_AGE);
      });
    }
  }

  return activeRiskFree;
};

export const setRiskFreeValue = (value = "5", maxAgeSeconds = DEFAULT_RISK_FREE_MAX_AGE) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue || !canUseDom() || !isRiskFreePromoLive()) {
    return;
  }

  try {
    window.sessionStorage.setItem(RISK_FREE_STORAGE_KEY, normalizedValue);
  } catch (e) {
    // Ignore storage failures.
  }

  RISK_FREE_COOKIE_NAMES.forEach((cookieName) => {
    setCookie(cookieName, normalizedValue, maxAgeSeconds);
  });

  dispatchPromoStateChange({ type: "risk-free", value: normalizedValue });
};

export const clearRiskFreeValue = () => {
  if (!canUseDom()) {
    return;
  }

  try {
    window.sessionStorage.removeItem(RISK_FREE_STORAGE_KEY);
  } catch (e) {
    // Ignore storage failures.
  }

  RISK_FREE_COOKIE_NAMES.forEach((cookieName) => {
    clearCookie(cookieName);
  });

  clearRiskFreeQueryValue();

  dispatchPromoStateChange({ type: "risk-free", value: "" });
};
